/**
 * Core primitives.
 *
 * The kit takes PRE-FORMATTED strings for anything money- or date-shaped. It
 * never imports the app's formatters, so it stays extractable to its own repo
 * the moment a second app consumes it (ADR-0005), and so there is exactly one
 * place — src/lib/format.ts — that decides how a rupee looks.
 */

import {
  forwardRef,
  type ButtonHTMLAttributes,
  type CSSProperties,
  type HTMLAttributes,
  type ReactNode,
} from 'react'
import { Icon, type IconName } from './Icon'
import { useXScroll } from './useXScroll'
import {
  DEFAULT_PAGE_SIZE,
  clampPage,
  pageCountOf,
  pageRangeMeta,
} from './pagination'

export type Tone = 'neutral' | 'profit' | 'loss' | 'warn' | 'info' | 'brand' | 'muted' | 'back' | 'lay'

const cx = (...parts: (string | false | null | undefined)[]) => parts.filter(Boolean).join(' ')
export { cx }

/* ---------------------------------------------------------------- button -- */

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger'
  size?: 'sm' | 'md' | 'lg'
  icon?: IconName
  iconAfter?: IconName
  /** Renders a spinner and blocks the click without changing the button's width. */
  busy?: boolean
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'secondary', size = 'md', icon, iconAfter, busy, children, className, disabled, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type="button"
      className={cx('w7-btn', className)}
      data-variant={variant}
      data-size={size === 'md' ? undefined : size}
      disabled={disabled || busy}
      aria-busy={busy || undefined}
      {...rest}
    >
      {busy ? <Spinner size={size === 'sm' ? 13 : 15} /> : icon ? <Icon name={icon} size={size === 'sm' ? 15 : 17} /> : null}
      {children}
      {iconAfter ? <Icon name={iconAfter} size={size === 'sm' ? 15 : 17} /> : null}
    </button>
  )
})

export interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  name: IconName
  /** Required — an icon-only control with no label is unusable with a screen reader. */
  label: string
  size?: 'sm' | 'md'
  active?: boolean
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { name, label, size = 'md', active, className, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type="button"
      className={cx('w7-iconbtn', className)}
      data-size={size === 'sm' ? 'sm' : undefined}
      data-active={active ? 'true' : undefined}
      aria-label={label}
      title={label}
      {...rest}
    >
      <Icon name={name} size={size === 'sm' ? 15 : 18} />
    </button>
  )
})

export function Spinner({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" className="w7-spin">
      <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeOpacity={0.25} strokeWidth={2.5} />
      <path
        d="M21 12a9 9 0 0 0-9-9"
        fill="none"
        stroke="currentColor"
        strokeWidth={2.5}
        strokeLinecap="round"
      />
    </svg>
  )
}

/* ------------------------------------------------------------------ card -- */

export interface CardProps extends Omit<HTMLAttributes<HTMLElement>, 'title'> {
  title?: ReactNode
  subtitle?: ReactNode
  /** Right-aligned controls in the header. */
  actions?: ReactNode
  footer?: ReactNode
  /** Removes body padding — for a card whose body is a table. */
  flush?: boolean
  as?: 'section' | 'div' | 'article'
}

export function Card({
  title,
  subtitle,
  actions,
  footer,
  flush,
  children,
  className,
  as: Tag = 'section',
  ...rest
}: CardProps) {
  return (
    <Tag className={cx('w7-card', className)} {...rest}>
      {title || actions ? (
        <header className="w7-card-head">
          <div className="w7-card-head-titles">
            <h3 className="w7-card-title">{title}</h3>
            {subtitle ? <p className="w7-card-sub">{subtitle}</p> : null}
          </div>
          {actions ? <div className="w7-row" style={{ marginLeft: 'auto' }}>{actions}</div> : null}
        </header>
      ) : null}
      <div className={cx('w7-card-body', flush && 'w7-card-body-flush')}>{children}</div>
      {footer ? <footer className="w7-card-foot">{footer}</footer> : null}
    </Tag>
  )
}

/* --------------------------------------------------------- scroll region -- */

export interface ScrollRegionProps extends Omit<HTMLAttributes<HTMLDivElement>, 'children'> {
  /** Names the pane. A scroller with no name is an unreachable box to a screen reader. */
  label: string
  /**
   * Bound the height. ONLY legitimate inside an overlay — a modal, drawer, menu
   * or command palette — which is viewport-bound by nature. Never pass it for a
   * table or panel on a page: constitution directive 14 gives this app exactly
   * one scrollbar, the document's.
   */
  maxHeight?: number | string
  children: ReactNode
}

/**
 * A pane that scrolls SIDEWAYS, when it has to, and can be reached from a keyboard.
 *
 * Four attributes have to travel together — `role="region"`, an `aria-label`,
 * `tabIndex={0}` and the overflow — and this console has several wide surfaces
 * that each need all four. A scroller without the tabIndex simply cannot be
 * scrolled by someone who does not use a mouse.
 *
 * An earlier version of this comment argued that `maxHeight` was load-bearing:
 * bound the height, and the pane becomes an internally scrolling grid whose
 * sticky header engages. True, and now forbidden — directive 14 (owner rule,
 * 2026-07-31) says the page scrolls and nothing inside it does. The header
 * still sticks; it sticks against the DOCUMENT instead, which works precisely
 * because this pane is no longer a scrollport.
 *
 * Horizontal overflow is the surviving exception, and it is applied only while
 * the content genuinely does not fit — see `useXScroll` for why it cannot
 * simply be left on.
 */
export function ScrollRegion({ label, maxHeight, children, className, style, ...rest }: ScrollRegionProps) {
  const xs = useXScroll<HTMLDivElement>()
  return (
    <div
      className={cx('w7-tablewrap', 'w7-scrollregion', className)}
      role="region"
      aria-label={label}
      ref={xs.ref}
      data-xscroll={xs.xScroll}
      // Only focusable when there is something to scroll: a tab stop that does
      // nothing is a tab stop a keyboard user has to pass through for no reason.
      tabIndex={xs.xScroll ? 0 : undefined}
      style={{ maxHeight, ...style }}
      {...rest}
    >
      {children}
    </div>
  )
}

/* ------------------------------------------------------------------- kbd -- */

/**
 * A key cap. The ⌘K hint appears in the topbar trigger, the palette footer
 * legend and the palette's empty state; three inline spans with hand-rolled
 * borders is precisely the hardcoded styling the token rules exist to stop.
 */
export function Kbd({ children }: { children: ReactNode }) {
  return <kbd className="w7-kbd">{children}</kbd>
}

/* ------------------------------------------------------------------ chip -- */

export interface ChipProps extends HTMLAttributes<HTMLSpanElement> {
  tone?: Tone
  size?: 'sm' | 'md'
  icon?: IconName
}

export function Chip({ tone = 'muted', size = 'md', icon, children, className, ...rest }: ChipProps) {
  return (
    <span
      className={cx('w7-chip', className)}
      data-tone={tone}
      data-size={size === 'sm' ? 'sm' : undefined}
      {...rest}
    >
      {icon ? <Icon name={icon} size={12} /> : null}
      {children}
    </span>
  )
}

/** Role badge — colour-coded by tier so the ladder is readable at a glance. */
export function RoleChip({ role, label }: { role: string; label: string }) {
  return (
    <span className="w7-role" data-role={role} title={label}>
      {label}
    </span>
  )
}

export function Dot({ tone = 'neutral', pulse }: { tone?: Tone; pulse?: boolean }) {
  return <span className="w7-dot" data-tone={tone} data-pulse={pulse ? 'true' : undefined} />
}

export function Avatar({ name, size = 'md' }: { name: string; size?: 'sm' | 'md' | 'lg' }) {
  const parts = name.trim().split(/[\s._-]+/).filter(Boolean)
  const text =
    parts.length >= 2
      ? `${parts[0]?.[0] ?? ''}${parts[1]?.[0] ?? ''}`
      : (parts[0] ?? '?').slice(0, 2)
  return (
    <span className="w7-avatar" data-size={size === 'md' ? undefined : size} aria-hidden="true">
      {text.toUpperCase()}
    </span>
  )
}

/* --------------------------------------------------------------- banners -- */

export interface BannerProps {
  tone?: 'info' | 'warn' | 'error'
  title?: ReactNode
  children?: ReactNode
  actions?: ReactNode
}

export function Banner({ tone = 'info', title, children, actions }: BannerProps) {
  const icon: IconName = tone === 'error' ? 'error' : tone === 'warn' ? 'warning' : 'info'
  return (
    <div className="w7-banner" data-tone={tone} role={tone === 'error' ? 'alert' : 'status'}>
      <Icon name={icon} size={17} />
      <div style={{ minWidth: 0 }}>
        {title ? <strong>{title}</strong> : null}
        {children ? <div>{children}</div> : null}
      </div>
      {actions ? <div className="w7-row" style={{ marginLeft: 'auto' }}>{actions}</div> : null}
    </div>
  )
}

/* ------------------------------------------------------------ empty/load -- */

export function Skeleton({
  width = '100%',
  height = 12,
  radius = 6,
}: {
  width?: number | string
  height?: number | string
  radius?: number
}) {
  return <span className="w7-skeleton" style={{ width, height, borderRadius: radius }} />
}

export interface StateBlockProps {
  icon?: IconName
  title: ReactNode
  children?: ReactNode
  actions?: ReactNode
  tone?: 'neutral' | 'error'
}

/** The one empty/error/no-access block. Every list and chart routes through it. */
export function StateBlock({ icon, title, children, actions, tone = 'neutral' }: StateBlockProps) {
  return (
    <div className="w7-state" role={tone === 'error' ? 'alert' : undefined}>
      {icon ? (
        <span className="w7-state-icon" data-tone={tone}>
          <Icon name={icon} size={22} />
        </span>
      ) : null}
      <p className="w7-state-title">{title}</p>
      {children ? <div className="w7-state-body">{children}</div> : null}
      {actions ? <div className="w7-row" style={{ marginTop: 4 }}>{actions}</div> : null}
    </div>
  )
}

/* ----------------------------------------------------------------- meter -- */

export interface MeterProps {
  /** 0..1, values above 1 render as an over-limit overflow rather than clipping. */
  value: number
  tone?: 'profit' | 'warn' | 'loss' | 'neutral'
  label?: string
  height?: number
}

/**
 * A horizontal usage bar. Above 100% the fill saturates and flips to the loss
 * tone — an account over its credit limit must look over, not merely full.
 */
export function Meter({ value, tone, label, height = 6 }: MeterProps) {
  const ratio = Number.isFinite(value) ? Math.max(0, value) : 0
  const resolved = tone ?? (ratio > 1 ? 'loss' : ratio > 0.8 ? 'warn' : 'profit')
  return (
    <span
      className="w7-meter"
      style={{ height }}
      role="meter"
      aria-valuenow={Math.round(ratio * 100)}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={label}
      title={label}
    >
      <span
        className="w7-meter-fill"
        data-tone={resolved === 'neutral' ? undefined : resolved}
        style={{ width: `${Math.min(100, ratio * 100)}%` }}
      />
    </span>
  )
}

/* ---------------------------------------------------------------- legend -- */

export interface LegendItem {
  label: string
  color: string
  value?: string
}

export function Legend({ items, onSelect }: { items: LegendItem[]; onSelect?: (index: number) => void }) {
  return (
    <ul className="w7-legend">
      {items.map((item, i) => (
        <li key={item.label} className="w7-legend-item">
          {onSelect ? (
            <button type="button" onClick={() => onSelect(i)} className="w7-legend-item">
              <span className="w7-legend-swatch" style={{ background: item.color }} />
              <span className="w7-truncate">{item.label}</span>
              {item.value ? <span className="w7-legend-value">{item.value}</span> : null}
            </button>
          ) : (
            <>
              <span className="w7-legend-swatch" style={{ background: item.color }} />
              <span className="w7-truncate">{item.label}</span>
              {item.value ? <span className="w7-legend-value">{item.value}</span> : null}
            </>
          )}
        </li>
      ))}
    </ul>
  )
}

/* ------------------------------------------------------------------- kpi -- */

export interface KpiProps {
  label: ReactNode
  /** Pre-formatted headline figure. */
  value: ReactNode
  /** Small print under the value — the context that makes the number mean something. */
  meta?: ReactNode
  /** Signed change, pre-formatted, e.g. "+12.4%". */
  delta?: string
  deltaTone?: Tone
  /** Rendered to the right of the value — usually a Sparkline. */
  spark?: ReactNode
  tone?: Tone
  accent?: boolean
  /** Full-precision value for the title attribute when `value` is abbreviated. */
  exact?: string
  onClick?: () => void
  size?: 'sm' | 'md'
}

/**
 * A KPI tile.
 *
 * Deliberately never a bare number: `meta` is not optional in spirit. The
 * product this replaces showed "10,20,566 / BALANCE" in a slab three times the
 * height of anything useful, with no denominator, no trend and no timestamp —
 * a number nobody could act on.
 */
export function Kpi({
  label,
  value,
  meta,
  delta,
  deltaTone = 'neutral',
  spark,
  tone,
  accent,
  exact,
  onClick,
  size = 'md',
}: KpiProps) {
  const Tag = onClick ? 'button' : 'div'
  return (
    <Tag
      className="w7-kpi"
      data-accent={accent ? 'true' : undefined}
      onClick={onClick}
      type={onClick ? 'button' : undefined}
    >
      <span className="w7-kpi-label">{label}</span>
      <span className="w7-row" style={{ gap: 10, alignItems: 'baseline' }}>
        <span
          className={cx(size === 'sm' ? 'w7-kpi-value-sm' : 'w7-kpi-value', tone && `w7-tone-${tone}`)}
          title={exact}
        >
          {value}
        </span>
        {delta ? <span className={cx('w7-delta', `w7-tone-${deltaTone}`)}>{delta}</span> : null}
        {spark ? <span className="w7-kpi-spark">{spark}</span> : null}
      </span>
      {meta ? <span className="w7-kpi-meta">{meta}</span> : null}
    </Tag>
  )
}

/* ---------------------------------------------------------------- layout -- */

export function PageHead({
  title,
  subtitle,
  actions,
  children,
}: {
  title: ReactNode
  subtitle?: ReactNode
  actions?: ReactNode
  children?: ReactNode
}) {
  return (
    <div className="w7-page-head">
      <div style={{ minWidth: 0 }}>
        <h1 className="w7-page-title">{title}</h1>
        {subtitle ? <p className="w7-card-sub">{subtitle}</p> : null}
      </div>
      {actions ? <div className="w7-page-head-actions">{actions}</div> : null}
      {children}
    </div>
  )
}

export interface Crumb {
  label: string
  href?: string
}

export function Breadcrumb({
  items,
  onNavigate,
}: {
  items: Crumb[]
  onNavigate?: (href: string) => void
}) {
  return (
    <nav className="w7-breadcrumb" aria-label="Upline">
      {items.map((item, i) => (
        <span key={`${item.label}-${i}`} className="w7-row" style={{ gap: 4 }}>
          {i > 0 ? <Icon name="chevron-right" size={13} /> : null}
          {item.href && onNavigate ? (
            <button type="button" onClick={() => onNavigate(item.href!)}>
              {item.label}
            </button>
          ) : (
            <span aria-current={i === items.length - 1 ? 'page' : undefined}>{item.label}</span>
          )}
        </span>
      ))}
    </nav>
  )
}

/* -------------------------------------------------------- tabs / segments -- */

export interface TabItem<T extends string = string> {
  id: T
  label: string
  count?: number
}

export function Tabs<T extends string>({
  items,
  value,
  onChange,
}: {
  items: TabItem<T>[]
  value: T
  onChange: (id: T) => void
}) {
  return (
    <div className="w7-tabs" role="tablist">
      {items.map((item) => (
        <button
          key={item.id}
          type="button"
          role="tab"
          className="w7-tab"
          aria-selected={item.id === value}
          onClick={() => onChange(item.id)}
        >
          {item.label}
          {item.count !== undefined ? <span className="w7-tab-count">{item.count}</span> : null}
        </button>
      ))}
    </div>
  )
}

export function Segmented<T extends string>({
  items,
  value,
  onChange,
  label,
}: {
  items: { id: T; label: string }[]
  value: T
  onChange: (id: T) => void
  label: string
}) {
  const activeIndex = Math.max(
    0,
    items.findIndex((item) => item.id === value),
  )
  return (
    <div
      className="w7-segmented"
      role="group"
      aria-label={label}
      style={
        {
          '--w7-seg-i': activeIndex,
          '--w7-seg-n': items.length,
          gridTemplateColumns: `repeat(${items.length}, minmax(0, 1fr))`,
        } as CSSProperties
      }
    >
      <span className="w7-segmented-thumb" aria-hidden="true" />
      {items.map((item) => (
        <button
          key={item.id}
          type="button"
          className="w7-segmented-item"
          aria-pressed={item.id === value}
          onClick={() => onChange(item.id)}
        >
          {item.label}
        </button>
      ))}
    </div>
  )
}

export function FilterChip({
  active,
  onClick,
  children,
  count,
}: {
  active: boolean
  onClick: () => void
  children: ReactNode
  count?: number
}) {
  return (
    <button type="button" className="w7-filterchip" aria-pressed={active} onClick={onClick}>
      {children}
      {count !== undefined ? <span className="w7-tab-count">{count}</span> : null}
    </button>
  )
}

/* ---------------------------------------------------------------- pager --- */

export function Pager({
  page,
  pageCount,
  onChange,
  total,
  pageSize = DEFAULT_PAGE_SIZE,
}: {
  page: number
  pageCount: number
  onChange: (page: number) => void
  total?: number
  pageSize?: number
}) {
  // Empty tables already have their own empty state — skip a barren bar.
  if (pageCount <= 1 && (total === undefined || total === 0)) return null

  const resolvedCount =
    total !== undefined ? pageCountOf(total, pageSize) : Math.max(1, pageCount)
  const safePage = clampPage(page, resolvedCount)

  const window: number[] = []
  const from = Math.max(1, Math.min(safePage - 2, resolvedCount - 4))
  const to = Math.min(resolvedCount, Math.max(safePage + 2, 5))
  for (let p = from; p <= to; p++) window.push(p)

  const range = total !== undefined ? pageRangeMeta(safePage, pageSize, total) : null

  const meta =
    total !== undefined
      ? total === 0
        ? 'No rows'
        : range
          ? `Showing ${range.first.toLocaleString('en-IN')}–${range.last.toLocaleString('en-IN')} of ${total.toLocaleString('en-IN')}`
          : `${total.toLocaleString('en-IN')} rows`
      : `Page ${safePage} of ${resolvedCount}`

  const go = (next: number) => {
    const clamped = clampPage(next, resolvedCount)
    if (clamped !== safePage) onChange(clamped)
  }

  // Always show arrow controls when there is data so the control is discoverable.
  // On a single page they stay visible but disabled.
  const showControls = total === undefined ? resolvedCount >= 1 : total > 0

  return (
    <nav className="w7-pager" aria-label="Pagination">
      <p className="w7-pager-meta">{meta}</p>
      {showControls ? (
        <div className="w7-pager-controls">
          <button
            type="button"
            className="w7-pagebtn w7-pagebtn-icon"
            onClick={() => go(1)}
            disabled={safePage <= 1}
            aria-label="First page"
            title="First page"
          >
            <Icon name="chevrons-left" size={16} />
          </button>
          <button
            type="button"
            className="w7-pagebtn w7-pagebtn-icon"
            onClick={() => go(safePage - 1)}
            disabled={safePage <= 1}
            aria-label="Previous page"
            title="Previous page"
          >
            <Icon name="arrow-left" size={16} />
          </button>
          {window.map((p) => (
            <button
              key={p}
              type="button"
              className="w7-pagebtn"
              aria-current={p === safePage ? 'page' : undefined}
              aria-label={`Page ${p}`}
              onClick={() => go(p)}
            >
              {p}
            </button>
          ))}
          <button
            type="button"
            className="w7-pagebtn w7-pagebtn-icon"
            onClick={() => go(safePage + 1)}
            disabled={safePage >= resolvedCount}
            aria-label="Next page"
            title="Next page"
          >
            <Icon name="arrow-right" size={16} />
          </button>
          <button
            type="button"
            className="w7-pagebtn w7-pagebtn-icon"
            onClick={() => go(resolvedCount)}
            disabled={safePage >= resolvedCount}
            aria-label="Last page"
            title="Last page"
          >
            <Icon name="chevrons-right" size={16} />
          </button>
        </div>
      ) : null}
    </nav>
  )
}

/* ------------------------------------------------------------ definition -- */

export function DefinitionList({
  items,
  columns = 2,
}: {
  items: { label: ReactNode; value: ReactNode }[]
  columns?: number
}) {
  return (
    <dl className="w7-dl" style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}>
      {items.map((item, i) => (
        <div key={i}>
          <dt>{item.label}</dt>
          <dd>{item.value}</dd>
        </div>
      ))}
    </dl>
  )
}
